import {useCallback, useMemo, useRef} from "react";
import {Stack, useTheme} from "@mui/material";
import {useParams} from "react-router-dom";
import {AgGridReact} from "ag-grid-react";
import {useTranslation} from "react-i18next";
import {useRecoilValue} from "recoil";
import dayjs from "dayjs";
import {ColDef, ICellRendererParams, IServerSideDatasource, ValueFormatterParams} from "ag-grid-community";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {fetchAttachmentsList} from "../../services/http.services";
import {practiceDetail} from "../../state";
import UploadAttachment from "../../common/upload-attachment/UploadAttachment";
import DownloadAttachment from "../../common/download-attachment/DownloadAttachment";
import DeleteAttachment from "../../common/delete-attachment/DeleteAttachment";

const AttachedInformation = (props: any) => {
    const theme = useTheme();
    const {t} = useTranslation(['expiredManagement']);
    const urlParams = useParams();

    const prDetail = useRecoilValue(practiceDetail);
    const gridRef = useRef<AgGridReact>(null);

    const columnDefs = useMemo<ColDef[]>(() => [
        {headerName: t('attachmentDetail.grid.filename'), field: 'filename'},
        {headerName: t('attachmentDetail.grid.type'), field: 'type'},
        {
            headerName: t('attachmentDetail.grid.createdDatetime'), field: 'createdDatetime',
            valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : '')
        },
        {
            headerName: t('attachmentDetail.grid.updatedDatetime'), field: 'updatedDatetime',
            valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : '')
        },
        {
            field: 'id',
            headerName: "",
            maxWidth: 100,
            minWidth: 100,
            pinned: 'right',
            resizable: false,
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <>
                        <DownloadAttachment attachment={{code: params?.data?.id, ...params?.data}} />
                        <DeleteAttachment id={params?.data?.id} postAction={refreshList}/>
                    </>
                );
            }
        },
    ], [t] );

    const refreshList = useCallback((route?: string[]) => {
        gridRef.current!.api.refreshServerSide({ route: route, purge: true });
    }, []);

    const serverSideDatasource: IServerSideDatasource = useMemo(() => ({
        getRows(params) {
            fetchAttachmentsList(urlParams.id as string)
                .then((response: any) => {
                    
                    if (response?.data?.length == 0) 
                        params.api.showNoRowsOverlay();
                        
                    params.success({
                        rowData: response.data,
                        rowCount: response.data.length
                    })
                })
                .catch(() => {
                    params.fail();
                });
        }
    }), []);

    return (
        <>
            <AgGridCustomTable
                gridRef={gridRef}
                rowModelType={'serverSide'}
                serverSideInfiniteScroll={true}
                serverSideDatasource={serverSideDatasource}
                columnDefs={columnDefs}
                gridOptions={
                    {
                        pagination: false,
                        defaultColDef: {
                            resizable: true,
                            lockPinned: true,
                            }
                    }
                }
                onGridReady={(event) => {
                    event.api.sizeColumnsToFit();
                }}
            />

            <Stack direction={{sm: 'row'}} gap={1}
                   sx={{display: "flex", justifyContent: "flex-end", my: theme.spacing(2)}}>
                <UploadAttachment expiredCode={prDetail.expiredCode} postAction={refreshList}/>
            </Stack>
        </>
    )
}

export default AttachedInformation;